// Minimal i18n hook — no external library needed.
// Dictionary sections:
//   worker.* / job.* / mat.* / check.* / work.* / app.* / sig.* / photo.* / toast.*
//     — Worker Dashboard + Job Detail (crew UI).
//   tp.*    — Tradesman Portal (public magic-link surface for cleaners, plumbers, etc).
//   pub.*   — Public read-only project share (landlord / tenant / stakeholder view).
//   inbox.* — Landlord Portfolio Approval Inbox (portfolio owners approving high-cost items).
//   common.* — Generic labels shared across surfaces.
import { useEffect, useState } from "react";

const DICTS = {
  en: {
    // Worker Dashboard
    "worker.today": "Today's Board",
    "worker.stats": "{active} active · {pool} available in pool · {scheduled} scheduled today",
    "worker.tab.today": "Today",
    "worker.tab.all": "All Jobs",
    "worker.loading": "Loading jobs...",
    "worker.empty.title": "Nothing scheduled",
    "worker.empty.sub": "Check All Jobs for the pool or unscheduled work.",
    "worker.empty.all.title": "No jobs available",
    "worker.empty.all.sub": "When admin creates a job, it will appear here.",
    "worker.section.today": "Scheduled Today",
    "worker.section.upcoming": "Upcoming Days",
    "worker.section.on_clock": "On the Clock",
    "worker.section.follow_up": "Needs Return",
    "worker.section.assigned": "Assigned to Me",
    "worker.section.pool": "Available Pool — Grab a Job",
    "worker.section.done": "Finished",
    "worker.optimize": "Optimize Route",
    "worker.open_maps": "Open in Maps",
    "worker.tech": "Tech",
    "worker.status.assigned": "Assigned",
    "worker.status.in_progress": "In Progress",
    "worker.status.completed": "Completed",
    "worker.status.follow_up": "Needs Follow-up",
    "worker.priority.low": "LOW",
    "worker.priority.normal": "NORMAL",
    "worker.priority.high": "HIGH",
    "worker.priority.urgent": "URGENT",
    "worker.unclaimed": "UNCLAIMED",
    "worker.customer": "Customer",
    "worker.clocked_in": "Clocked in",
    "worker.issues": "ISSUES",
    // Job Detail — core
    "job.loading": "Loading job...",
    "job.back": "Back",
    "job.clock_in": "Clock In",
    "job.resume_work": "Resume Work",
    "job.clock_out": "Clock Out (Break)",
    "job.on_clock_since": "On the clock since",
    "job.location": "Location",
    "job.complete": "Complete",
    "job.follow_up": "Follow-up",
    "job.claim": "Claim This Job",
    "job.release": "Release Back to Pool",
    "job.pool_title": "This job is in the pool",
    "job.pool_sub": 'Tap "Claim This Job" above to add it to your list. Once claimed you can clock in, take photos, and check off issues.',
    "job.completed": "Job Completed",
    "job.internal_pdf": "Internal PDF",
    "job.customer_pdf": "Customer PDF",
    "job.get_signature": "Get Customer Signature",
    "job.tab.work": "Work",
    "job.tab.issues": "Issues",
    "job.tab.materials": "Materials",
    "job.tab.appliances": "Appliances",
    "job.tab.ai": "AI Help",
    "job.before": "Before Media",
    "job.after": "After Media",
    "job.photo": "Photo",
    "job.video": "Video",
    "job.crew": "Crew on this job",
    "job.lead": "Lead",
    "job.helper": "Helper",
    "job.followup_msg": "This job is flagged for follow-up. Clock in again to resume work.",
    "job.time_entries": "Time Entries (per-person)",
    "job.on_clock": "on clock",
    "job.customer": "Customer",
    "job.entry_access": "Entry / Access",
    "job.entry_code": "Code",
    "job.lockbox": "Lockbox",
    "job.items": "items",
    "job.marked": "Marked",
    "job.exit.title": "Leaving the job site?",
    "job.exit.body": "You're still on the clock but away from the property. Clock out, or let the office know you're on a materials run.",
    "job.exit.clock_out": "Clock Out — I'm done here",
    "job.exit.material_run": "Getting Materials (keep clock running)",
    "job.exit.dismiss": "Not now",
    "job.exit.footer": "We'll ask again in 15 minutes if you're still off-site.",
    "job.material_run_logged": "Material run logged — clock stays running",
    "job.material_runs": "Off-Site Events",
    "job.material_run_start": "Material Run",
    "job.material_run_return": "Back On-Site",
    // Signature
    "sig.card_title": "Customer Sign-Off",
    "sig.redo": "Redo",
    "sig.signed_by": "Signed by",
    "sig.confirm_remove": "Remove this signature? The customer will need to sign again.",
    "sig.removed": "Signature removed",
    // Photo tag picker
    "photo.tag_photo": "Tag Photo",
    "photo.tap_toggle": "Tap to add / remove",
    "photo.done": "Done",
    "photo.tag_title": "Tag photo",
    "photo.markup_title": "Mark up photo",
    // Materials
    "mat.add_material": "Add Material",
    "mat.scan_receipt": "Scan Receipt (Auto-Fill)",
    "mat.reading_receipt": "Reading Receipt...",
    "mat.truck_stock": "Use From Truck Stock",
    "mat.manual": "Or enter manually",
    "mat.item_name": 'Item name (e.g., PVC Elbow 1/2")',
    "mat.qty": "Qty",
    "mat.unit_cost": "Unit $",
    "mat.add_item": "Add Item",
    "mat.items": "Items",
    "mat.receipts": "Receipts",
    "mat.add_receipt": "Add Receipt",
    "mat.name_required": "Name required",
    "mat.material_added": "Material added",
    // Checklist
    "check.title": "Issues to Fix",
    "check.empty": "No issues yet. Add complaints below as you inspect the property.",
    "check.new_placeholder": "Add a new issue (e.g., Leaky faucet in bathroom)",
    "check.fixed": "Fixed",
    // Work performed
    "work.title": "Work Performed",
    "work.placeholder": "Replaced compressor start relay, checked refrigerant lines, verified cooling at 38F...",
    "work.dictate_hint": "Tap to dictate hands-free · appended to notes",
    "work.saving": "Saving...",
    "work.save": "Save Notes",
    "work.polishing": "Polishing...",
    "work.polish": "Polish with AI",
    "work.ai_writeup": "AI Write-up (auto-attached on Complete)",
    "work.copy": "Copy",
    // Appliances
    "app.scanner": "AI Nameplate Scanner",
    "app.scanner_sub": "Snap a photo of the appliance data-plate. AI reads brand, model, serial, and estimates age.",
    "app.reading": "Reading Nameplate...",
    "app.scan": "Scan Nameplate",
    "app.empty": 'No appliances scanned yet. Tap "Scan Nameplate" above to capture your first one.',
    "app.confirm_remove": "Remove this appliance scan?",
    "app.removed": "Appliance removed",
    // Toasts
    "toast.clocked_in": "Clocked in!",
    "toast.clocked_out": "Clocked out",
    "toast.saved": "Saved",
    "lang.toggle.tooltip": "Change language",
    // ── Common ─────────────────────────────────────────────────
    "common.loading": "Loading…",
    "common.back": "Back",
    // ── Tradesman Portal ───────────────────────────────────────
    "tp.header_link": "{trade} Link",
    "tp.filter.open": "Open",
    "tp.filter.complete": "Complete",
    "tp.filter.all": "All Items",
    "tp.search": "Search room or item",
    "tp.priority.urgent": "URGENT",
    "tp.priority.high": "HIGH",
    "tp.priority.med": "MED",
    "tp.priority.low": "LOW",
    "tp.status.open": "Open",
    "tp.status.in_progress": "In Progress",
    "tp.status.complete": "Complete",
    "tp.btn.camera": "Camera",
    "tp.btn.gallery": "Gallery",
    "tp.btn.inprog": "In Prog",
    "tp.btn.complete": "Complete",
    "tp.need_more": "⚠️ Need {n} more photo{plural}",
    "tp.pin_title": "Field Radar · Secure Link",
    "tp.pin_body": "Enter the 4-digit PIN you were sent to unlock this link on this device. You'll only have to do this once.",
    "tp.pin_unlock": "Unlock",
    "tp.pin_wrong": "Wrong PIN — try again.",
    "tp.link_broken": "Link Not Working",
    "tp.link_broken_body": "Ask the person who sent it for a fresh link.",
    "tp.all_caught_up": "All caught up 🎉",
    "tp.nothing_matches": "Nothing matches this filter.",
    "tp.marked_complete": "Marked complete ✓",
    "tp.marked_inprogress": "Moved to In Progress",
    "tp.photos_added": "{n} photo{plural} added from {source}",
    "tp.source.camera": "camera",
    "tp.source.gallery": "gallery",
    "tp.upload_fail": "Couldn't upload — try again",
    "tp.status_fail": "Couldn't update — try again",
    "tp.footer": "Powered by Field Radar · fieldradar.pro",
    "tp.signout": "Sign out this device",
    "tp.signout_confirm": "Sign out this device? You'll need the link (and PIN, if set) to get back in.",
    "tp.signed_out_title": "Signed out",
    "tp.signed_out_body": "This device has been forgotten. Open your magic link again to come back — you'll need your PIN if the office set one.",
    "tp.shared_warning": "Heads up — the founder may have seen this link opened on other devices.",
    "tp.unlocked": "Unlocked",
    "tp.room_items": "{room} · {n} item{plural}",
    "tp.trade.cleaner": "Cleaner",
    "tp.trade.plumber": "Plumber",
    "tp.trade.electrician": "Electrician",
    "tp.trade.painter": "Painter",
    "tp.trade.hvac": "HVAC",
    "tp.trade.handyman": "Handyman",
    "tp.trade.landscaper": "Landscaper",
    "tp.trade.roofer": "Roofer",
    "tp.trade.water_heater": "Water Heater",
    "tp.trade.general_sub": "Field Crew",
    // ── Public read-only project share ─────────────────────────
    "pub.readonly": "🔗 Read-Only · Field Radar",
    "pub.kpi.total": "Total",
    "pub.kpi.open": "Open",
    "pub.kpi.in_progress": "In Prog",
    "pub.kpi.complete": "Complete",
    "pub.back_units": "Back to Units",
    "pub.back_rooms": "Room View",
    "pub.tap_tile": "Tap a tile to view its checklist",
    "pub.units": "🏠 Units ({n})",
    "pub.common_areas": "🚪 Building Common Areas ({n})",
    "pub.exterior": "🌳 Exterior",
    "pub.all_units": "🏢 All Units",
    "pub.do_not_enter": "🚫 Occupied — Do Not Enter",
    "pub.occupied": "🚫 Occupied",
    "pub.no_items": "No punch items for {room}.",
    "pub.done": "done",
    "pub.needed": "needed",
    "pub.new_find": "🆕 New Find",
    "pub.pending": "⏳ Pending",
    "pub.approved": "✓ Approved",
    "pub.rejected": "✗ Rejected",
    "pub.pm_verified": "👁 PM Verified",
    "pub.project_only": "🔒 Project",
    "pub.expired": "Link invalid or expired",
    "pub.expired_body": "Invalid or expired link",
    "pub.all_items": "📋 All Items ({n})",
    "pub.footer": "Powered by Field Radar · Read-Only",
    "pub.status.open": "Open",
    // ── ProjectRadar founder admin (pr.*) ─────────────────────
    "pr.kpi.total": "Total",
    "pr.kpi.open": "Open",
    "pr.kpi.in_progress": "In Prog",
    "pr.kpi.complete": "Complete",
    "pr.kpi.est_cost": "Est Cost",
    "pr.status.open": "Open",
    "pr.status.in_progress": "In Prog",
    "pr.status.deferred": "Deferred",
    "pr.status.complete": "Complete",
    "pr.punch.add_open": "Add Punch Item",
    "pr.punch.title_placeholder": "What needs fixing? (e.g. Patch drywall behind bathroom door)",
    "pr.punch.room_dash": "— Room —",
    "pr.punch.assignee_placeholder": "Assignee (opt)",
    "pr.punch.cost_placeholder": "Est cost $ (opt)",
    "pr.punch.photos_placeholder": "📸 Required photos",
    "pr.punch.notes_placeholder": "Notes (optional)",
    "pr.punch.project_only_auto": "🔒 Project-only (auto-set for All Units)",
    "pr.punch.project_only": "🔒 Project-only — skip Maintenance dispatch",
    "pr.punch.new_find": "🆕 Discovered post-walkthrough (new find)",
    "pr.punch.needs_approval": "📝 Needs landlord approval (extra scope)",
    "pr.punch.priority_suffix": "{p} priority",
    "pr.btn.cancel": "Cancel",
    "pr.btn.save": "Save",
    "pr.btn.add": "Add",
    "pr.btn.adding": "Adding…",
    "pr.btn.saving": "Saving…",
    "pr.btn.uploading": "Uploading…",
    "pr.album.title": "📸 Project Album",
    "pr.album.tab.before": "before",
    "pr.album.tab.after": "after",
    "pr.album.tab.other": "other",
    "pr.album.upload_prefix": "+ Upload {tab}",
    "pr.album.empty": "No {tab} photos yet · tap upload above",
    "pr.album.paired": "🔗 Paired",
    "pr.album.tap_compare": "Tap to compare",
    "pr.docs.title": "🔒 Docs (Founder-Only)",
    "pr.docs.sub": "Never shown to subs, teammates, or landlords",
    "pr.docs.upload": "+ Upload Doc",
    "pr.docs.amended_pdf": "📄 Amended Quote PDF",
    "pr.codes.building_door": "Building door code",
    "pr.codes.laundry_key": "Laundry-room key ID",
    "pr.codes.keys_note": "Keys-location note (property-wide)",
    "pr.codes.per_unit": "Per-unit — push-button (numbered door) · key ID (letter door) · note",
    "pr.codes.save": "Save Codes",
    "pr.codes.admin_only": "🔒 Admin + Landlord Portal only · Never shown in public share links",
    "pr.codes.pb_placeholder": "push-button",
    "pr.codes.key_placeholder": "key ID",
    "pr.codes.note_placeholder": "note (optional)",
    "pr.priority.low": "low",
    "pr.priority.med": "med",
    "pr.priority.high": "high",
    "pr.priority.urgent": "urgent",
    "pr.tt.mark_complete": "Mark complete",
    "pr.tt.reopen": "Reopen",
    "pr.tt.photo_camera": "Take a photo (camera)",
    "pr.tt.photo_gallery": "Choose from camera roll / gallery",
    "pr.tt.delete": "Delete",
    "pr.tt.enable_dispatch": "Click to allow maintenance dispatch for this item",
    "pr.tt.mark_project_only": "Mark as project-only (skip maintenance dispatch)",
    "pr.tt.verified_by": "Verified by {who}",
    "pr.badge.new_find": "🆕 New Find",
    "pr.badge.pending_approval": "⏳ Pending Approval",
    "pr.badge.approved": "✓ Approved",
    "pr.badge.rejected": "✗ Rejected",
    "pr.badge.pm_verified": "👁 PM Verified",
    "pr.badge.project_only": "🔒 Project Only",
    "pr.badge.mark_project_only": "mark project-only",
    "pr.badge.quote_needed": "📋 Quote Needed",
    "pr.badge.photos_need_more": "📸 {count}/{min} — need {gap} more",
    "pr.badge.photos_ok": "📸 {count}/{min} ✓",
    "pr.detail.added": "Added",
    "pr.detail.updated": "Updated",
    "pr.detail.completed": "Completed",
    "pr.detail.approved_by": "Approved · by {who}",
    "pr.detail.discovery": "Discovery",
    "pr.detail.quote": "Quote",
    "pr.detail.assignee": "Assignee",
    "pr.detail.assignee_placeholder": "Field worker or sub (e.g. LCV)",
    "pr.detail.min_photos": "Min photos",
    "pr.transition_prefix": "→ {label}",
    "pr.confirm.delete_punch": "Remove this punch item?",
    "pr.toast.photo_added": "Photo added",
    "pr.toast.upload_failed": "Upload failed",
    "pr.toast.couldnt_update": "Couldn't update",
    "pr.toast.couldnt_delete": "Couldn't delete",
    "pr.toast.title_required": "Title required",
    "pr.toast.couldnt_add": "Couldn't add",
    // Voice Notes
    "vn.dictate_es": "🎤 Dictate in Spanish",
    "vn.listening": "🔴 Listening…",
    "vn.stop": "■ Stop",
    "vn.translating": "Translating…",
    "vn.retry": "Retry",
    "vn.original_es": "Original (Spanish)",
    "vn.translation_en": "English translation",
    "vn.unsupported": "Voice input isn't supported on this browser. Try Chrome or Safari on mobile.",
    "vn.no_transcript": "Didn't catch anything — try again",
    "vn.translate_fail": "Translation failed — the Spanish text is still saved",
    "vn.use_translation": "Use translation",
    "vn.mic_hint": "Speak in Spanish · we'll translate to English for the office",


    "pub.status.in_progress": "In Prog",
    "pub.status.deferred": "Deferred",
    "pub.status.complete": "Complete",
    // ── Landlord Approval Inbox ────────────────────────────────
    "inbox.title": "Approval Inbox",
    "inbox.portfolio": "Portfolio",
    "inbox.waiting_on_you": "Waiting on you",
    "inbox.awaiting_quote": "📋 {n} awaiting quote",
    "inbox.all_properties": "All properties",
    "inbox.tab.pending": "Pending",
    "inbox.tab.approved": "Approved",
    "inbox.tab.rejected": "Rejected",
    "inbox.tab.all": "All",
    "inbox.search": "Search title, address, room…",
    "inbox.select_all": "Select All ({n})",
    "inbox.clear": "Clear ({n})",
    "inbox.bulk_note": "Bulk note (optional)…",
    "inbox.approve": "Approve",
    "inbox.reject": "Reject",
    "inbox.approve_n": "Approve {n}",
    "inbox.reject_n": "Reject {n}",
    "inbox.note_show": "+ Note",
    "inbox.note_hide": "Hide note",
    "inbox.note_placeholder": "Optional note to PMR…",
    "inbox.zero": "Inbox zero",
    "inbox.zero_sub": "Any new discovery over ${cap} will land here for your call.",
    "inbox.no_matches": "No matches",
    "inbox.no_matches_sub": "Try a different tab or clear filters.",
    "inbox.quote_needed": "📋 Quote Needed",
    "inbox.over_cap": "Over ${cap}",
    "inbox.open_project": "🔗 Open Project",
    "inbox.note_prefix": "Note:",
    "inbox.pending": "⏳ Pending",
    "inbox.approved": "✓ Approved",
    "inbox.rejected": "✗ Rejected",
    "inbox.decided": "{label} {n} · {skipped} skipped",
    "inbox.invalid_link": "Invalid link",
    "inbox.expired_toast": "Invalid or expired link",
    "inbox.decide_fail": "Couldn't record decisions",
    "inbox.footer": "Powered by Field Radar · Portfolio Inbox",
    "inbox.new_find": "🆕 New Find",

    // Worker Job View v4 — phone-optimized single-page tech UI
    "jv4.job": "Job",
    "jv4.status.in_progress": "In progress",
    "jv4.status.assigned": "Assigned",
    "jv4.status.completed": "Completed",
    "jv4.status.follow_up": "Follow-up",
    "jv4.status.pool": "In pool",
    "jv4.assigned_to": "Assigned to",
    "jv4.clocked_in_at": "Clocked in {time}",
    "jv4.not_clocked_in": "Not clocked in yet",
    "jv4.on_the_way": "On the way",
    "jv4.on_the_way_sub_none": "Tap an ETA below to tell office you're rolling",
    "jv4.on_the_way_sub_sent": "ETA {eta} min · office notified",
    "jv4.on_the_way_sub_sent_no_eta": "Office notified",
    "jv4.eta_minutes": "{n}m",
    "jv4.update_eta": "Update ETA",
    "jv4.cant_get_in": "Can't get in",
    "jv4.timer": "Timer",
    "jv4.timer.running": "Running",
    "jv4.timer.stopped": "Stopped",
    "jv4.timer.started_at": "Started at {time}",
    "jv4.timer.on_lunch": "On lunch since {time}",
    "jv4.clock_in": "Clock in",
    "jv4.clock_out": "Clock out",
    "jv4.lunch": "Lunch",
    "jv4.end_lunch": "End lunch",
    "jv4.materials_run": "Materials run",
    "jv4.photos": "Photos",
    "jv4.photos.before": "Before",
    "jv4.photos.after": "After",
    "jv4.photos.summary": "{before} before · {after} after",
    "jv4.photos.need_after": "1 after photo needed to close",
    "jv4.photos.ok": "Enough photos to close",
    "jv4.photos.add_before": "Add before",
    "jv4.photos.add_after": "Add after",
    "jv4.receipt": "Receipt",
    "jv4.receipt.count": "{n} attached",
    "jv4.receipt.none": "Not added — attach or mark no purchase",
    "jv4.receipt.no_purchase": "No purchase — {reason}",
    "jv4.receipt.add": "+ Add receipt",
    "jv4.receipt.mark_none": "No purchase",
    "jv4.notes": "What did you do?",
    "jv4.notes.placeholder": "Say what you did — parts used, what you fixed, any issues left…",
    "jv4.notes.ok": "{n} / 30 characters ✓",
    "jv4.notes.short": "{n} / 30 characters — need {more} more",
    "jv4.notes.autosave": "Auto-saves as you type",
    "jv4.notes.saving": "Saving…",
    "jv4.notes.saved": "Saved",
    "jv4.mark_done": "Mark done",
    "jv4.mark_done.unlock": "Add {items} to unlock",
    "jv4.mark_done.after_photo": "1 after photo",
    "jv4.mark_done.receipt": "receipt",
    "jv4.mark_done.notes": "notes",
    "jv4.reopen": "Reopen job",
    "jv4.follow_up": "Mark follow-up",
    "jv4.more": "More",
    "jv4.more.property_notes": "Property notes & entry code",
    "jv4.more.chat": "Chat with office",
    "jv4.more.timeline": "Timeline & history",
    "jv4.more.quote": "Quote / billing",
    "jv4.more.classic": "Open full (classic) view",
    "jv4.admin.title": "Admin controls",
    "jv4.admin.desc": "Only admins see these. Techs never see this drawer.",
    "jv4.admin.habitability": "Habitability flag",
    "jv4.admin.habitability_sub": "Starts SHHA 30-day clock",
    "jv4.admin.location_gate": "Location gate",
    "jv4.admin.location_gate_sub": "Require tech to be within radius to clock in",
    "jv4.admin.triage_link": "Public triage link",
    "jv4.admin.triage_link_sub": "Self-serve tenant status URL",
    "jv4.admin.mint": "Mint",
    "jv4.claim": "Claim this job",
    "jv4.release": "Release to pool",
    "jv4.pool_hint": "This job is in the pool. Claim it to start.",
  },
  es: {
    "worker.today": "Trabajo de Hoy",
    "worker.stats": "{active} activos · {pool} disponibles en pool · {scheduled} programados hoy",
    "worker.tab.today": "Hoy",
    "worker.tab.all": "Todos los Trabajos",
    "worker.loading": "Cargando trabajos...",
    "worker.empty.title": "Nada programado",
    "worker.empty.sub": "Revisa Todos los Trabajos para el pool o trabajos sin programar.",
    "worker.empty.all.title": "No hay trabajos disponibles",
    "worker.empty.all.sub": "Cuando el administrador cree un trabajo, aparecerá aquí.",
    "worker.section.today": "Programado para Hoy",
    "worker.section.upcoming": "Próximos Días",
    "worker.section.on_clock": "En Reloj",
    "worker.section.follow_up": "Requiere Retorno",
    "worker.section.assigned": "Asignados a Mí",
    "worker.section.pool": "Pool Disponible — Toma un Trabajo",
    "worker.section.done": "Terminado",
    "worker.optimize": "Optimizar Ruta",
    "worker.open_maps": "Abrir en Maps",
    "worker.tech": "Técnico",
    "worker.status.assigned": "Asignado",
    "worker.status.in_progress": "En Proceso",
    "worker.status.completed": "Completado",
    "worker.status.follow_up": "Requiere Seguimiento",
    "worker.priority.low": "BAJA",
    "worker.priority.normal": "NORMAL",
    "worker.priority.high": "ALTA",
    "worker.priority.urgent": "URGENTE",
    "worker.unclaimed": "SIN ASIGNAR",
    "worker.customer": "Cliente",
    "worker.clocked_in": "Reloj activo",
    "worker.issues": "ISSUES",
    "job.loading": "Cargando trabajo...",
    "job.back": "Atrás",
    "job.clock_in": "Iniciar Reloj",
    "job.resume_work": "Reanudar Trabajo",
    "job.clock_out": "Parar Reloj (Descanso)",
    "job.on_clock_since": "En reloj desde",
    "job.location": "Ubicación",
    "job.complete": "Completar",
    "job.follow_up": "Seguimiento",
    "job.claim": "Tomar Este Trabajo",
    "job.release": "Devolver al Pool",
    "job.pool_title": "Este trabajo está en el pool",
    "job.pool_sub": 'Toca "Tomar Este Trabajo" arriba para añadirlo a tu lista. Una vez tomado puedes iniciar reloj, tomar fotos, y marcar issues.',
    "job.completed": "Trabajo Completado",
    "job.internal_pdf": "PDF Interno",
    "job.customer_pdf": "PDF Cliente",
    "job.get_signature": "Firma del Cliente",
    "job.tab.work": "Trabajo",
    "job.tab.issues": "Issues",
    "job.tab.materials": "Materiales",
    "job.tab.appliances": "Aparatos",
    "job.tab.ai": "Ayuda IA",
    "job.before": "Fotos Antes",
    "job.after": "Fotos Después",
    "job.photo": "Foto",
    "job.video": "Video",
    "job.crew": "Equipo en este trabajo",
    "job.lead": "Líder",
    "job.helper": "Ayudante",
    "job.followup_msg": "Este trabajo requiere seguimiento. Inicia reloj nuevamente para continuar.",
    "job.time_entries": "Registro de Horas (por persona)",
    "job.on_clock": "en reloj",
    "job.customer": "Cliente",
    "job.entry_access": "Entrada / Acceso",
    "job.entry_code": "Código",
    "job.lockbox": "Caja de Llaves",
    "job.items": "elementos",
    "job.marked": "Marcado",
    "job.exit.title": "¿Saliendo del trabajo?",
    "job.exit.body": "Aún estás en reloj pero lejos de la propiedad. Para el reloj, o avísale a la oficina que vas por materiales.",
    "job.exit.clock_out": "Parar Reloj — Ya terminé aquí",
    "job.exit.material_run": "Voy por Materiales (dejar reloj activo)",
    "job.exit.dismiss": "Ahora no",
    "job.exit.footer": "Te preguntaremos otra vez en 15 minutos si sigues fuera del sitio.",
    "job.material_run_logged": "Salida por materiales registrada — reloj sigue activo",
    "job.material_runs": "Eventos Fuera del Sitio",
    "job.material_run_start": "Por Materiales",
    "job.material_run_return": "De Regreso",
    "sig.card_title": "Firma del Cliente",
    "sig.redo": "Rehacer",
    "sig.signed_by": "Firmado por",
    "sig.confirm_remove": "¿Eliminar esta firma? El cliente tendrá que firmar de nuevo.",
    "sig.removed": "Firma eliminada",
    "photo.tag_photo": "Etiquetar Foto",
    "photo.tap_toggle": "Toca para añadir / quitar",
    "photo.done": "Listo",
    "photo.tag_title": "Etiquetar foto",
    "photo.markup_title": "Marcar foto",
    "mat.add_material": "Añadir Material",
    "mat.scan_receipt": "Escanear Recibo (Auto)",
    "mat.reading_receipt": "Leyendo Recibo...",
    "mat.truck_stock": "Usar del Stock del Camión",
    "mat.manual": "O introducir manualmente",
    "mat.item_name": 'Nombre del item (ej: Codo PVC 1/2")',
    "mat.qty": "Cant",
    "mat.unit_cost": "Costo $",
    "mat.add_item": "Añadir Item",
    "mat.items": "Items",
    "mat.receipts": "Recibos",
    "mat.add_receipt": "Añadir Recibo",
    "mat.name_required": "Nombre requerido",
    "mat.material_added": "Material añadido",
    "check.title": "Issues por Reparar",
    "check.empty": "Aún no hay issues. Añade abajo lo que encuentres al inspeccionar la propiedad.",
    "check.new_placeholder": "Añadir nuevo issue (ej: Grifo goteando en el baño)",
    "check.fixed": "Arreglado",
    "work.title": "Trabajo Realizado",
    "work.placeholder": "Reemplacé el relé de arranque del compresor, revisé líneas de refrigerante, verifiqué enfriamiento a 38F...",
    "work.dictate_hint": "Toca para dictar sin usar las manos · se añade a las notas",
    "work.saving": "Guardando...",
    "work.save": "Guardar Notas",
    "work.polishing": "Puliendo...",
    "work.polish": "Pulir con IA",
    "work.ai_writeup": "Reporte IA (se adjunta al completar)",
    "work.copy": "Copiar",
    "app.scanner": "Escáner IA de Placa",
    "app.scanner_sub": "Toma una foto de la placa del aparato. La IA lee marca, modelo, serie y estima la edad.",
    "app.reading": "Leyendo Placa...",
    "app.scan": "Escanear Placa",
    "app.empty": 'Aún no hay aparatos escaneados. Toca "Escanear Placa" arriba para capturar el primero.',
    "app.confirm_remove": "¿Eliminar este escaneo de aparato?",
    "app.removed": "Aparato eliminado",
    "toast.clocked_in": "¡Reloj iniciado!",
    "toast.clocked_out": "Reloj parado",
    "toast.saved": "Guardado",
    "pr.kpi.total": "Total",
    "pr.kpi.open": "Abiertos",
    "pr.kpi.in_progress": "En Prog",
    "pr.kpi.complete": "Completos",
    "pr.kpi.est_cost": "Costo Est.",
    "pr.status.open": "Abierto",
    "pr.status.in_progress": "En Prog",
    "pr.status.deferred": "Diferido",
    "pr.status.complete": "Completo",
    "pr.punch.add_open": "Añadir Tarea de Punch",
    "pr.punch.title_placeholder": "¿Qué hay que arreglar? (ej. Reparar drywall detrás de la puerta del baño)",
    "pr.punch.room_dash": "— Cuarto —",
    "pr.punch.assignee_placeholder": "Asignado a (opc.)",
    "pr.punch.cost_placeholder": "Costo est. $ (opc.)",
    "pr.punch.photos_placeholder": "📸 Fotos requeridas",
    "pr.punch.notes_placeholder": "Notas (opcional)",
    "pr.punch.project_only_auto": "🔒 Solo Proyecto (automático para Todas las Unidades)",
    "pr.punch.project_only": "🔒 Solo Proyecto — omitir envío a Mantenimiento",
    "pr.punch.new_find": "🆕 Descubierto después del recorrido (nuevo hallazgo)",
    "pr.punch.needs_approval": "📝 Requiere aprobación del dueño (fuera del alcance)",
    "pr.punch.priority_suffix": "prioridad {p}",
    "pr.btn.cancel": "Cancelar",
    "pr.btn.save": "Guardar",
    "pr.btn.add": "Añadir",
    "pr.btn.adding": "Añadiendo…",
    "pr.btn.saving": "Guardando…",
    "pr.btn.uploading": "Subiendo…",
    "pr.album.title": "📸 Álbum del Proyecto",
    "pr.album.tab.before": "antes",
    "pr.album.tab.after": "después",
    "pr.album.tab.other": "otras",
    "pr.album.upload_prefix": "+ Subir {tab}",
    "pr.album.empty": "Aún no hay fotos de {tab} · toca subir arriba",
    "pr.album.paired": "🔗 Emparejada",
    "pr.album.tap_compare": "Toca para comparar",
    "pr.docs.title": "🔒 Docs (Solo Dueño)",
    "pr.docs.sub": "Nunca se muestra a subs, empleados ni dueños",
    "pr.docs.upload": "+ Subir Documento",
    "pr.docs.amended_pdf": "📄 PDF Cotización Modificada",
    "pr.codes.building_door": "Código de puerta del edificio",
    "pr.codes.laundry_key": "ID de llave del cuarto de lavado",
    "pr.codes.keys_note": "Nota de ubicación de llaves (toda la propiedad)",
    "pr.codes.per_unit": "Por unidad — botón (puerta numerada) · ID llave (puerta letra) · nota",
    "pr.codes.save": "Guardar Códigos",
    "pr.codes.admin_only": "🔒 Solo Admin + Portal del Dueño · Nunca se muestra en enlaces públicos",
    "pr.codes.pb_placeholder": "botón numérico",
    "pr.codes.key_placeholder": "ID de llave",
    "pr.codes.note_placeholder": "nota (opcional)",
    "pr.priority.low": "baja",
    "pr.priority.med": "media",
    "pr.priority.high": "alta",
    "pr.priority.urgent": "urgente",

    "lang.toggle.tooltip": "Cambiar idioma",
    "common.loading": "Cargando…",
    "common.back": "Atrás",
    "tp.header_link": "Enlace de {trade}",
    "tp.filter.open": "Abiertos",
    "tp.filter.complete": "Completos",
    "tp.filter.all": "Todos",
    "tp.search": "Buscar cuarto o tarea",
    "tp.priority.urgent": "URGENTE",
    "tp.priority.high": "ALTA",
    "tp.priority.med": "MED",
    "tp.priority.low": "BAJA",
    "tp.status.open": "Abierto",
    "tp.status.in_progress": "En Progreso",
    "tp.status.complete": "Completo",
    "tp.btn.camera": "Cámara",
    "tp.btn.gallery": "Galería",
    "tp.btn.inprog": "En Prog",
    "tp.btn.complete": "Completar",
    "tp.need_more": "⚠️ Faltan {n} foto{plural}",
    "tp.pin_title": "Field Radar · Enlace Seguro",
    "tp.pin_body": "Ingresa el PIN de 4 dígitos que te enviaron para desbloquear este enlace en este dispositivo. Solo tendrás que hacerlo una vez.",
    "tp.pin_unlock": "Desbloquear",
    "tp.pin_wrong": "PIN incorrecto — inténtalo de nuevo.",
    "tp.link_broken": "Enlace No Funciona",
    "tp.link_broken_body": "Pídele a quien te lo envió un enlace nuevo.",
    "tp.all_caught_up": "Todo al día 🎉",
    "tp.nothing_matches": "Nada coincide con este filtro.",
    "tp.marked_complete": "Marcado completo ✓",
    "tp.marked_inprogress": "Movido a En Progreso",
    "tp.photos_added": "{n} foto{plural} añadida{plural} desde {source}",
    "tp.source.camera": "cámara",
    "tp.source.gallery": "galería",
    "tp.upload_fail": "No se pudo subir — inténtalo de nuevo",
    "tp.status_fail": "No se pudo actualizar — inténtalo de nuevo",
    "tp.footer": "Con tecnología de Field Radar · fieldradar.pro",
    "tp.signout": "Cerrar sesión en este dispositivo",
    "tp.signout_confirm": "¿Cerrar sesión en este dispositivo? Necesitarás el enlace (y el PIN, si hay) para volver.",
    "tp.signed_out_title": "Sesión cerrada",
    "tp.signed_out_body": "Este dispositivo fue olvidado. Abre tu enlace de nuevo para volver — necesitarás tu PIN si la oficina lo configuró.",
    "tp.shared_warning": "Aviso — el dueño puede haber visto este enlace abierto en otros dispositivos.",
    "tp.unlocked": "Desbloqueado",
    "tp.room_items": "{room} · {n} tarea{plural}",
    "tp.trade.cleaner": "Limpieza",
    "tp.trade.plumber": "Plomero",
    "tp.trade.electrician": "Electricista",
    "tp.trade.painter": "Pintor",
    "tp.trade.hvac": "HVAC",
    "tp.trade.handyman": "Mantenimiento",
    "tp.trade.landscaper": "Jardinero",
    "tp.trade.roofer": "Techero",
    "tp.trade.water_heater": "Calentador de Agua",
    "tp.trade.general_sub": "Cuadrilla",
    "pub.readonly": "🔗 Solo Lectura · Field Radar",
    "pub.kpi.total": "Total",
    "pub.kpi.open": "Abiertos",
    "pub.kpi.in_progress": "En Prog",
    "pub.kpi.complete": "Completos",
    "pub.back_units": "Volver a Unidades",
    "pub.back_rooms": "Vista de Cuartos",
    "pub.tap_tile": "Toca una casilla para ver su lista",
    "pr.tt.mark_complete": "Marcar completo",
    "pr.tt.reopen": "Reabrir",
    "pr.tt.photo_camera": "Tomar foto (cámara)",
    "pr.tt.photo_gallery": "Elegir de galería",
    "pr.tt.delete": "Eliminar",
    "pr.tt.enable_dispatch": "Toca para permitir envío a Mantenimiento",
    "pr.tt.mark_project_only": "Marcar como solo-proyecto (omitir Mantenimiento)",
    "pr.tt.verified_by": "Verificado por {who}",
    "pr.badge.new_find": "🆕 Nuevo Hallazgo",
    "pr.badge.pending_approval": "⏳ Aprobación Pendiente",
    "pr.badge.approved": "✓ Aprobado",
    "pr.badge.rejected": "✗ Rechazado",
    "pr.badge.pm_verified": "👁 Verificado por PM",
    "pr.badge.project_only": "🔒 Solo Proyecto",
    "pr.badge.mark_project_only": "marcar solo-proyecto",
    "pr.badge.quote_needed": "📋 Cotización Requerida",
    "pr.badge.photos_need_more": "📸 {count}/{min} — faltan {gap}",
    "pr.badge.photos_ok": "📸 {count}/{min} ✓",
    "pr.detail.added": "Añadido",
    "pr.detail.updated": "Actualizado",
    "pr.detail.completed": "Completado",
    "pr.detail.approved_by": "Aprobado · por {who}",
    "pr.detail.discovery": "Descubrimiento",
    "pr.detail.quote": "Cotización",
    "pr.detail.assignee": "Asignado",
    "pr.detail.assignee_placeholder": "Trabajador o subcontratista (ej. LCV)",
    "pr.detail.min_photos": "Fotos min.",
    "pr.transition_prefix": "→ {label}",
    "pr.confirm.delete_punch": "¿Eliminar esta tarea de punch?",
    "pr.toast.photo_added": "Foto añadida",
    "pr.toast.upload_failed": "Subida falló",
    "pr.toast.couldnt_update": "No se pudo actualizar",
    "pr.toast.couldnt_delete": "No se pudo eliminar",
    "pr.toast.title_required": "Título requerido",
    "pr.toast.couldnt_add": "No se pudo añadir",
    "vn.dictate_es": "🎤 Dictar en Español",
    "vn.listening": "🔴 Escuchando…",
    "vn.stop": "■ Parar",
    "vn.translating": "Traduciendo…",
    "vn.retry": "Reintentar",
    "vn.original_es": "Original (Español)",
    "vn.translation_en": "Traducción al Inglés",
    "vn.unsupported": "Este navegador no soporta dictado. Usa Chrome o Safari en el móvil.",
    "vn.no_transcript": "No entendí nada — inténtalo de nuevo",
    "vn.translate_fail": "La traducción falló — el texto en español se guardó",
    "vn.use_translation": "Usar traducción",
    "vn.mic_hint": "Habla en español · traducimos al inglés para la oficina",

    "pub.units": "🏠 Unidades ({n})",
    "pub.common_areas": "🚪 Áreas Comunes del Edificio ({n})",
    "pub.exterior": "🌳 Exterior",
    "pub.all_units": "🏢 Todas las Unidades",
    "pub.do_not_enter": "🚫 Ocupado — No Entrar",
    "pub.occupied": "🚫 Ocupado",
    "pub.no_items": "No hay tareas para {room}.",
    "pub.done": "listos",
    "pub.needed": "necesarias",
    "pub.new_find": "🆕 Nuevo Hallazgo",
    "pub.pending": "⏳ Pendiente",
    "pub.approved": "✓ Aprobado",
    "pub.rejected": "✗ Rechazado",
    "pub.pm_verified": "👁 Verificado por PM",
    "pub.project_only": "🔒 Proyecto",
    "pub.expired": "Enlace inválido o expirado",
    "pub.expired_body": "Enlace inválido o expirado",
    "pub.all_items": "📋 Todos los Elementos ({n})",
    "pub.footer": "Con tecnología de Field Radar · Solo Lectura",
    "pub.status.open": "Abierto",
    "pub.status.in_progress": "En Prog",
    "pub.status.deferred": "Diferido",
    "pub.status.complete": "Completo",
    "inbox.title": "Bandeja de Aprobación",
    "inbox.portfolio": "Portafolio",
    "inbox.waiting_on_you": "Esperando por ti",
    "inbox.awaiting_quote": "📋 {n} esperando cotización",
    "inbox.all_properties": "Todas las propiedades",
    "inbox.tab.pending": "Pendientes",
    "inbox.tab.approved": "Aprobados",
    "inbox.tab.rejected": "Rechazados",
    "inbox.tab.all": "Todos",
    "inbox.search": "Buscar título, dirección, cuarto…",
    "inbox.select_all": "Seleccionar Todos ({n})",
    "inbox.clear": "Limpiar ({n})",
    "inbox.bulk_note": "Nota grupal (opcional)…",
    "inbox.approve": "Aprobar",
    "inbox.reject": "Rechazar",
    "inbox.approve_n": "Aprobar {n}",
    "inbox.reject_n": "Rechazar {n}",
    "inbox.note_show": "+ Nota",
    "inbox.note_hide": "Ocultar nota",
    "inbox.note_placeholder": "Nota opcional para PMR…",
    "inbox.zero": "Bandeja al día",
    "inbox.zero_sub": "Cualquier descubrimiento nuevo mayor a ${cap} aparecerá aquí para tu decisión.",
    "inbox.no_matches": "Sin coincidencias",
    "inbox.no_matches_sub": "Prueba otra pestaña o limpia los filtros.",
    "inbox.quote_needed": "📋 Cotización Requerida",
    "inbox.over_cap": "Sobre ${cap}",
    "inbox.open_project": "🔗 Abrir Proyecto",
    "inbox.note_prefix": "Nota:",
    "inbox.pending": "⏳ Pendiente",
    "inbox.approved": "✓ Aprobado",
    "inbox.rejected": "✗ Rechazado",
    "inbox.decided": "{label} {n} · {skipped} omitidos",
    "inbox.invalid_link": "Enlace inválido",
    "inbox.expired_toast": "Enlace inválido o expirado",
    "inbox.decide_fail": "No se pudieron registrar las decisiones",
    "inbox.footer": "Con tecnología de Field Radar · Portafolio",
    "inbox.new_find": "🆕 Nuevo Hallazgo",

    // Worker Job View v4 (Spanish)
    "jv4.job": "Trabajo",
    "jv4.status.in_progress": "En curso",
    "jv4.status.assigned": "Asignado",
    "jv4.status.completed": "Completado",
    "jv4.status.follow_up": "Seguimiento",
    "jv4.status.pool": "En el pool",
    "jv4.assigned_to": "Asignado a",
    "jv4.clocked_in_at": "Entrada a las {time}",
    "jv4.not_clocked_in": "Sin registrar entrada",
    "jv4.on_the_way": "En camino",
    "jv4.on_the_way_sub_none": "Toque una hora para avisar a la oficina",
    "jv4.on_the_way_sub_sent": "ETA {eta} min · oficina avisada",
    "jv4.on_the_way_sub_sent_no_eta": "Oficina avisada",
    "jv4.eta_minutes": "{n}m",
    "jv4.update_eta": "Actualizar ETA",
    "jv4.cant_get_in": "No puedo entrar",
    "jv4.timer": "Cronómetro",
    "jv4.timer.running": "En curso",
    "jv4.timer.stopped": "Detenido",
    "jv4.timer.started_at": "Inició a las {time}",
    "jv4.timer.on_lunch": "En almuerzo desde {time}",
    "jv4.clock_in": "Registrar entrada",
    "jv4.clock_out": "Registrar salida",
    "jv4.lunch": "Almuerzo",
    "jv4.end_lunch": "Fin almuerzo",
    "jv4.materials_run": "Viaje materiales",
    "jv4.photos": "Fotos",
    "jv4.photos.before": "Antes",
    "jv4.photos.after": "Después",
    "jv4.photos.summary": "{before} antes · {after} después",
    "jv4.photos.need_after": "Necesita 1 foto de después antes de cerrar",
    "jv4.photos.ok": "Suficientes fotos para cerrar",
    "jv4.photos.add_before": "Agregar antes",
    "jv4.photos.add_after": "Agregar después",
    "jv4.receipt": "Recibo",
    "jv4.receipt.count": "{n} adjuntos",
    "jv4.receipt.none": "Sin adjuntar — agregue o marque sin compra",
    "jv4.receipt.no_purchase": "Sin compra — {reason}",
    "jv4.receipt.add": "+ Agregar recibo",
    "jv4.receipt.mark_none": "Sin compra",
    "jv4.notes": "¿Qué hizo?",
    "jv4.notes.placeholder": "Diga qué hizo — piezas usadas, qué reparó, problemas pendientes…",
    "jv4.notes.ok": "{n} / 30 caracteres ✓",
    "jv4.notes.short": "{n} / 30 caracteres — faltan {more}",
    "jv4.notes.autosave": "Se guarda automáticamente",
    "jv4.notes.saving": "Guardando…",
    "jv4.notes.saved": "Guardado",
    "jv4.mark_done": "Marcar terminado",
    "jv4.mark_done.unlock": "Agregue {items} para desbloquear",
    "jv4.mark_done.after_photo": "1 foto de después",
    "jv4.mark_done.receipt": "recibo",
    "jv4.mark_done.notes": "notas",
    "jv4.reopen": "Reabrir trabajo",
    "jv4.follow_up": "Marcar seguimiento",
    "jv4.more": "Más",
    "jv4.more.property_notes": "Notas de propiedad y código de entrada",
    "jv4.more.chat": "Chat con la oficina",
    "jv4.more.timeline": "Cronología e historial",
    "jv4.more.quote": "Cotización / facturación",
    "jv4.more.classic": "Abrir vista completa (clásica)",
    "jv4.admin.title": "Controles del administrador",
    "jv4.admin.desc": "Solo los administradores ven esto. Los técnicos no ven este panel.",
    "jv4.admin.habitability": "Habitabilidad",
    "jv4.admin.habitability_sub": "Inicia el reloj de 30 días de SHHA",
    "jv4.admin.location_gate": "Filtro de ubicación",
    "jv4.admin.location_gate_sub": "El técnico debe estar dentro del radio para entrar",
    "jv4.admin.triage_link": "Enlace público de triage",
    "jv4.admin.triage_link_sub": "URL de estado autoservicio para el inquilino",
    "jv4.admin.mint": "Generar",
    "jv4.claim": "Tomar este trabajo",
    "jv4.release": "Devolver al pool",
    "jv4.pool_hint": "Este trabajo está en el pool. Tómelo para comenzar.",
  },
};

// Auto-detect language on first visit: browser Spanish → default to es.
// Explicit user choice (localStorage lang) always wins on subsequent visits.
function _detectInitialLang() {
  try {
    const stored = localStorage.getItem("lang");
    if (stored === "en" || stored === "es") return stored;
    const nav = (navigator.language || navigator.userLanguage || "en").toLowerCase();
    if (nav.startsWith("es")) return "es";
    return "en";
  } catch { return "en"; }
}

let currentLang = _detectInitialLang();
const listeners = new Set();

export function getLang() { return currentLang; }
export function setLang(lang) {
  if (lang !== "en" && lang !== "es") return;
  currentLang = lang;
  try { localStorage.setItem("lang", lang); } catch {}
  listeners.forEach(fn => fn(lang));
}

export function useT() {
  const [lang, setLangState] = useState(currentLang);
  useEffect(() => {
    const fn = (l) => setLangState(l);
    listeners.add(fn);
    return () => listeners.delete(fn);
  }, []);
  const t = (key, vars) => {
    let s = (DICTS[lang] && DICTS[lang][key]) || (DICTS.en && DICTS.en[key]) || key;
    if (vars) {
      for (const k of Object.keys(vars)) s = s.replaceAll(`{${k}}`, String(vars[k]));
    }
    return s;
  };
  return { t, lang, setLang };
}

/**
 * Self-contained EN/ES toggle for PUBLIC surfaces (tradesman portal, public
 * share, landlord inbox) — no auth, no api call, purely localStorage.
 * The AdminShell has its own LangToggle that additionally PATCHes the
 * user's stored language server-side.
 */
export function PublicLangToggle({ dark = true }) {
  const { lang } = useT();
  const change = (next) => { if (next !== lang) setLang(next); };
  const base = dark
    ? "border border-slate-600 text-white"
    : "border border-[#0f172a] text-[#0f172a]";
  const activeCls = dark ? "bg-[#00C4CC] border-[#00C4CC]" : "bg-[#0f172a] text-white";
  return (
    <div className={`inline-flex ${base}`} data-testid="public-lang-toggle">
      <button onClick={() => change("en")} data-testid="public-lang-en"
        className={`px-2 h-7 text-[10px] font-black uppercase tracking-widest ${lang === "en" ? activeCls : ""}`}>EN</button>
      <button onClick={() => change("es")} data-testid="public-lang-es"
        className={`px-2 h-7 text-[10px] font-black uppercase tracking-widest ${lang === "es" ? activeCls : ""}`}>ES</button>
    </div>
  );
}
